/**
 * The membership-rule builder: "Match lists where [ALL|ANY] of these are
 * true" + condition chips + a live "→ N match" chip resolved server-side
 * (debounced, zero writes).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Loader2 } from 'lucide-react';

import { SegmentedPills } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { StaticTag } from '@/hooks/smswiser/static-tags/useStaticTags';

import type {
  FlatListGroupRule,
  ListGroupCondition,
} from '../../list-groups.types';
import { AddConditionMenu } from './AddConditionMenu';
import { ConditionChip } from './ConditionChip';

interface MatchState {
  count: number | null;
  loading: boolean;
}

interface Props {
  rule: FlatListGroupRule;
  staticTags: StaticTag[];
  countryKey?: string;
  match: MatchState;
  onChange: (rule: FlatListGroupRule) => void;
}

export const RuleBuilder: FC<Props> = ({ rule, staticTags, match, onChange, countryKey }) => {
  const setCondition = (index: number, next: ListGroupCondition) =>
    onChange({
      ...rule,
      nodes: rule.nodes.map((c, i) => (i === index ? next : c)),
    });
  const removeCondition = (index: number) =>
    onChange({ ...rule, nodes: rule.nodes.filter((_, i) => i !== index) });
  // One list selection per rule (14/10/2026): a lists condition replaces the rule's selection in place.
  const listsAt = rule.nodes.findIndex((c) => c.type === 'lists');
  const addCondition = (condition: ListGroupCondition) =>
    condition.type === 'lists' && listsAt >= 0
      ? setCondition(listsAt, condition)
      : onChange({ ...rule, nodes: [...rule.nodes, condition] });
  const selectedLists = listsAt >= 0 ? (rule.nodes[listsAt] as { listIds: number[] }) : undefined;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-muted/20 p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span>Match lists where</span>
        <SegmentedPills
          options={[
            { value: 'and', label: 'ALL' },
            { value: 'or', label: 'ANY' },
          ]}
          value={rule.type}
          onChange={(type) => onChange({ ...rule, type })}
        />
        <span>of these are true:</span>
        <span className="grow" />
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold',
            match.loading
              ? 'border-border text-muted-foreground'
              : match.count
                ? 'border-success/40 bg-success/10 text-success'
                : 'border-border bg-muted/40 text-muted-foreground',
          )}
          data-testid="match-chip"
        >
          {match.loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>→ {match.count ?? '—'} match</>
          )}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {rule.nodes.map((condition, index) => (
          <ConditionChip
            key={`${condition.type}-${index}`}
            condition={condition}
            staticTags={staticTags}
            countryKey={countryKey}
            onChange={(next) => setCondition(index, next)}
            onRemove={() => removeCondition(index)}
          />
        ))}
        <AddConditionMenu staticTags={staticTags} countryKey={countryKey} selectedLists={selectedLists} onAdd={addCondition} />
      </div>
      {rule.nodes.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          A group with no conditions never matches anything — add at least one.
        </p>
      ) : null}
    </div>
  );
};
